import {useState, useCallback, forwardRef} from '@wordpress/element';
import {useSelect, useDispatch} from '@wordpress/data';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {getSetting} from '@woocommerce/settings';
import {STORE_KEY} from '@ppcp/block-data';
import {isAdminOnly} from "../../utils";
import {useLoadPayPalScript} from "../../hooks";
import {CvvIcon, FastlaneLink, FastlaneModal, PaymentMethodLabel} from "../../components";
import {useCardBreakpoints, useCardFields, useCardFormHandler, useCardOptions, useCreatePaymentMethod} from "./hooks";
import {CreditCardProvider} from "./contexts";
import {useCreditCardContext} from "./hooks/use-credit-card-context";
import TokenizedCard from './tokenized-card';
import classnames from "classnames";

const data = getSetting('ppcp_card_data');

const PaymentMethodComponent = (props) => {
    const [modalOpen, setModalOpen] = useState(false);
    const queryParams = getSetting('paypalQueryParams');
    const {
        billing,
        shippingData
    } = props;

    const {email} = billing.billingAddress;

    const paypal = useLoadPayPalScript(queryParams);

    const {
        setPaymentToken
    } = useDispatch(STORE_KEY);

    const {
        emails,
        fastlane,
        paymentToken,
        customerFound,
    } = useSelect(select => {
        const store = select(STORE_KEY);
        return {
            emails: store.getCanceledEmails(),
            fastlane: store.getFastlane(),
            paymentToken: store.getPaymentToken(),
            customerFound: store.isCustomerFound()
        };
    });

    const onTokenize = useCallback((token) => {
        setPaymentToken(token);
    }, []);

    const showSignup = data.showSignup && !emails.includes(email);

    if (paymentToken) {
        return (
            <TokenizedCard
                data={data}
                token={paymentToken}
                fastlane={fastlane}
                customerFound={customerFound}
                {...props}/>
        )
    }

    return (
        <>
            {(showSignup && fastlane) &&
                <FastlaneLink
                    text={data.i18n.fastlane_signup}
                    logo={data.fastlane_logo}
                    onClick={() => setModalOpen(true)}/>
            }
            {fastlane &&
                <FastlaneModal
                    i18n={data.i18n}
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    fastlane={fastlane}
                    shippingData={shippingData}
                    onTokenize={onTokenize}/>
            }
            <CreditCardProvider data={data}>
                <CreditCardForm data={data} paypal={paypal} {...props}/>
            </CreditCardProvider>
        </>
    )
}

const CreditCardForm = ({data, paypal, ...props}) => {
    const {
        i18n,
        cardHolderNameEnabled
    } = data;

    const {
        errors,
        cardFieldRef
    } = useCreditCardContext();

    const {
        setCardContainerRef
    } = useCardBreakpoints();

    useCardFormHandler({
        data,
        paypal,
        ...props
    });

    return (
        <div className={'wc-ppcp-card-payment-method__container wc-payment-form'} ref={setCardContainerRef}>
            {cardHolderNameEnabled && (
                <div className="ppcp-card-fields--container card-name-field">
                    <div className="ppcp-card-field--container card-name-field">
                        <CardField id="ppcp-card-name" name={'name'} label={i18n.cardHolderLabel}></CardField>
                    </div>
                </div>
            )}
            <div className="ppcp-card-fields--container">
                <div className="ppcp-card-field--container card-number-field">
                    <CardField id="ppcp-card-number" name={'number'} label={i18n.cardNumberLabel}></CardField>
                    <ErrorMessage name={'number'} errors={errors}
                    />
                </div>
                <div className="ppcp-card-field--container card-exp-field">
                    <CardField id="ppcp-card-exp" name={'expiry'} label={i18n.cardExpiryLabel}/>
                    <ErrorMessage name={'expiry'} errors={errors}/>
                </div>
                <div className="ppcp-card-field--container card-cvv-field">
                    <CardField
                        id={'ppcp-card-cvv'}
                        name={'cvv'}
                        label={i18n.cardCvvLabel}>
                        <div className="ppcp-card-cvv-icon">
                            <CvvIcon/>
                        </div>
                    </CardField>
                    <ErrorMessage name={'cvv'} errors={errors}/>
                </div>
            </div>
        </div>
    )
}

const CardField = ({id, name, label, children}) => {
    const {
        errors,
        cardFieldRef
    } = useCreditCardContext();
    const hasError = (key, errors) => {
        return typeof errors === 'object' && errors.hasOwnProperty(key);
    }
    return (
        <>
            <label className="ppcp-card-field--label">
                {label}
            </label>
            <div id={id} ref={cardFieldRef(name)} className={classnames({'card-field--invalid': hasError(name, errors)})}>
                {children}
            </div>
        </>
    )
}

const ErrorMessage = ({name, errors}) => {
    if (errors?.[name]) {
        return (
            <div className="ppcp-card-field-error" data-parent="ppcp-card-exp">
                {data.i18n.error_codes[errors[name]]}
            </div>
        )
    }
    return null;
}

registerPaymentMethod({
    name: 'ppcp_card',
    ariaLabel: 'Credit/Debit Cards',
    label: <PaymentMethodLabel id={'ppcp_card'} title={data.title} icons={data.icon}/>,
    canMakePayment: () => {
        return !isAdminOnly();
    },
    content: <PaymentMethodComponent data={data}/>,
    edit: <PaymentMethodComponent data={data}/>,
    //placeOrderButtonLabel: data.i18n.buttonLabel,
    supports: {
        showSavedCards: true,
        showSaveOption: data.showSaveOption,
        features: data.features
    }
});